import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { ApiError } from '@/models/apiError'
import {
  CoexSignupInput,
  CreateWhatsappTemplateInput,
  UpdateWhatsappTemplateInput,
  WhatsappBillingStatus,
  WhatsappTemplate,
  WhatsappTemplateHistory,
  WhatsappTemplateMediaUpload,
  WhatsappTemplatePurpose,
} from '@/models/whatsappTemplate'
import {
  API_ERRORS,
  ENDPOINTS,
  UTILS,
} from '@/shared/constants'
import api from '@/shared/api'
import useRouting from './useRouting'

export default function useWhatsappOfficialApi() {
  const { getRouteWithId } = useRouting()
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [billingStatus, setBillingStatus] =
    useState<WhatsappBillingStatus | null>(null)
  const [isBillingLoading, setIsBillingLoading] = useState(false)

  /**
   * Padroniza mensagens de erro dos endpoints oficiais, preservando os códigos
   * já traduzidos no frontend.
   */
  const handleError = useCallback((fallback: string, error: unknown) => {
    const apiError = error as ApiError
    const errorCode = apiError.response?.data?.errorCode

    toast.error(errorCode && API_ERRORS[errorCode] ? API_ERRORS[errorCode] : fallback)
  }, [])

  /**
   * Carrega templates locais vinculados à integração oficial da empresa.
   */
  const getTemplates = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.get(ENDPOINTS.WHATSAPP_OFFICIAL.GET.TEMPLATES)
      setTemplates(response.data)
      return response.data as WhatsappTemplate[]
    } catch (error) {
      handleError('Erro ao buscar templates', error)
    } finally {
      setIsLoading(false)
    }
  }, [handleError])

  const getTemplate = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true)
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.GET.TEMPLATE,
          id,
          UTILS.PARAMS.ID
        )
        const response = await api.get(route)
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao buscar template', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getRouteWithId, handleError]
  )

  const uploadTemplateMedia = useCallback(
    async (file: File, example = false) => {
      try {
        setIsLoading(true)
        const formData = new FormData()
        formData.append('file', file)
        const endpoint = example
          ? ENDPOINTS.WHATSAPP_OFFICIAL.POST.TEMPLATE_EXAMPLE_MEDIA
          : ENDPOINTS.WHATSAPP_OFFICIAL.POST.TEMPLATE_MEDIA
        const response = await api.post(endpoint, formData)
        return response.data as WhatsappTemplateMediaUpload
      } catch (error) {
        handleError('Erro ao enviar anexo do template', error)
      } finally {
        setIsLoading(false)
      }
    },
    [handleError]
  )

  /**
   * Puxa os templates diretamente da Meta e atualiza o estado local da tela.
   */
  const syncTemplates = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.post(
        ENDPOINTS.WHATSAPP_OFFICIAL.POST.SYNC_TEMPLATES
      )
      setTemplates(response.data)
      toast.success('Templates sincronizados')
      return response.data as WhatsappTemplate[]
    } catch (error) {
      handleError('Erro ao sincronizar templates', error)
    } finally {
      setIsLoading(false)
    }
  }, [handleError])

  /**
   * Cria um rascunho local de template antes de submetê-lo à Meta.
   */
  const createTemplate = useCallback(
    async (data: CreateWhatsappTemplateInput) => {
      try {
        const response = await api.post(
          ENDPOINTS.WHATSAPP_OFFICIAL.POST.TEMPLATES,
          data
        )
        toast.success('Template criado')
        await getTemplates()
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao criar template', error)
      }
    },
    [getTemplates, handleError]
  )

  const createConversationStartPreset = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.post(
        ENDPOINTS.WHATSAPP_OFFICIAL.POST.CONVERSATION_START_PRESET
      )
      toast.success('Template sugerido criado como rascunho')
      await getTemplates()
      return response.data as WhatsappTemplate
    } catch (error) {
      handleError('Erro ao criar o template sugerido', error)
    } finally {
      setIsLoading(false)
    }
  }, [getTemplates, handleError])

  const createSystemTemplatePreset = useCallback(
    async (purpose: WhatsappTemplatePurpose) => {
      try {
        setIsLoading(true)
        const endpoint =
          ENDPOINTS.WHATSAPP_OFFICIAL.POST.SYSTEM_TEMPLATE_PRESET.replace(
            UTILS.PARAMS.PURPOSE,
            purpose
          )
        const response = await api.post(endpoint)
        toast.success('Template sugerido criado como rascunho')
        await getTemplates()
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao criar o template sugerido', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getTemplates, handleError]
  )

  /**
   * Envia um template local para aprovação na WABA.
   */
  const submitTemplate = useCallback(
    async (id: number) => {
      try {
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.POST.SUBMIT_TEMPLATE,
          id.toString(),
          UTILS.PARAMS.ID
        )
        const response = await api.post(route)
        toast.success('Template enviado para aprovação')
        await getTemplates()
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao submeter template', error)
      }
    },
    [getRouteWithId, getTemplates, handleError]
  )

  const updateTemplate = useCallback(
    async (id: number, data: UpdateWhatsappTemplateInput) => {
      try {
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.PATCH.TEMPLATE,
          id.toString(),
          UTILS.PARAMS.ID
        )
        const response = await api.patch(route, data)
        toast.success('Template atualizado')
        await getTemplates()
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao atualizar template', error)
      }
    },
    [getRouteWithId, getTemplates, handleError]
  )

  const cloneTemplate = useCallback(
    async (id: number, name: string) => {
      try {
        setIsLoading(true)
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.POST.CLONE_TEMPLATE,
          id.toString(),
          UTILS.PARAMS.ID
        )
        const response = await api.post(route, { name })
        toast.success('Template clonado como rascunho')
        await getTemplates()
        return response.data as WhatsappTemplate
      } catch (error) {
        handleError('Erro ao clonar template', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getRouteWithId, getTemplates, handleError]
  )

  const removeTemplate = useCallback(
    async (id: number) => {
      try {
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.DELETE.TEMPLATE,
          id.toString(),
          UTILS.PARAMS.ID
        )
        await api.delete(route)
        toast.success('Template excluído')
        await getTemplates()
        return true
      } catch (error) {
        handleError('Erro ao excluir template', error)
        return false
      }
    },
    [getRouteWithId, getTemplates, handleError]
  )

  const getTemplateHistory = useCallback(
    async (id: number) => {
      try {
        const route = getRouteWithId(
          ENDPOINTS.WHATSAPP_OFFICIAL.GET.TEMPLATE_HISTORY,
          id.toString(),
          UTILS.PARAMS.ID
        )
        const response = await api.get(route)
        return response.data as WhatsappTemplateHistory[]
      } catch (error) {
        handleError('Erro ao buscar histórico do template', error)
        return []
      }
    },
    [getRouteWithId, handleError]
  )

  /**
   * Conclui o cadastro incorporado após o redirect/OAuth da Meta.
   */
  const finishCoexSignup = useCallback(
    async (data: CoexSignupInput) => {
      try {
        const response = await api.post(
          ENDPOINTS.WHATSAPP_OFFICIAL.POST.COEX_SIGNUP,
          data
        )
        toast.success('API oficial configurada')
        return response.data
      } catch (error) {
        handleError('Erro ao concluir cadastro oficial', error)
      }
    },
    [handleError]
  )

  const getBillingStatus = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setIsBillingLoading(true)
        const response = await api.get(
          ENDPOINTS.WHATSAPP_OFFICIAL.GET.BILLING_STATUS
        )
        setBillingStatus(response.data)
        return response.data as WhatsappBillingStatus
      } catch (error) {
        if (!silent) {
          handleError('Erro ao consultar o pagamento do WhatsApp', error)
        }
      } finally {
        if (!silent) setIsBillingLoading(false)
      }
    },
    [handleError]
  )

  const confirmBillingConfigured = useCallback(async () => {
    try {
      setIsBillingLoading(true)
      const response = await api.post(
        ENDPOINTS.WHATSAPP_OFFICIAL.POST.CONFIRM_BILLING_STATUS
      )
      setBillingStatus(response.data)
      toast.success(
        'Configuração informada. O próximo envio de template confirmará a liberação pela Meta.'
      )
      return response.data as WhatsappBillingStatus
    } catch (error) {
      handleError('Erro ao confirmar o pagamento do WhatsApp', error)
    } finally {
      setIsBillingLoading(false)
    }
  }, [handleError])

  return {
    templates,
    isLoading,
    getTemplates,
    getTemplate,
    uploadTemplateMedia,
    syncTemplates,
    createTemplate,
    createConversationStartPreset,
    createSystemTemplatePreset,
    submitTemplate,
    updateTemplate,
    cloneTemplate,
    removeTemplate,
    getTemplateHistory,
    finishCoexSignup,
    billingStatus,
    isBillingLoading,
    getBillingStatus,
    confirmBillingConfigured,
  }
}
